/**
 * Copyright 2025 John Huikku Alienrobot LLC
 * www.ALIENROBOT.com
 */

import { getApiKey } from '../api-keys'
import type { Chunk } from './chunker'

export interface Embedding {
  chunkId: string
  vector: number[]
  model: string
  dim: number
  createdAt: number
}

/**
 * Generate embeddings using OpenAI API
 */
export async function generateEmbedding(
  text: string,
  model: string = 'text-embedding-3-small'
): Promise<number[]> {
  const apiKey = getApiKey('openai')
  
  if (!apiKey) {
    throw new Error('OpenAI API key not found. Please add it in Settings.')
  }

  const response = await fetch('https://api.openai.com/v1/embeddings', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`
    },
    body: JSON.stringify({
      input: text,
      model,
      encoding_format: 'float'
    })
  })

  if (!response.ok) {
    const error = await response.text()
    throw new Error(`OpenAI embedding failed: ${error}`)
  }

  const data = await response.json()
  return data.data[0].embedding
}

/**
 * Generate embeddings for multiple chunks with progress callback
 */
export async function generateEmbeddings(
  chunks: Chunk[],
  onProgress?: (current: number, total: number) => void,
  model: string = 'text-embedding-3-small'
): Promise<Embedding[]> {
  const embeddings: Embedding[] = []
  const total = chunks.length

  for (let i = 0; i < chunks.length; i++) {
    const chunk = chunks[i]
    
    try {
      const vector = await generateEmbedding(chunk.content, model)
      
      embeddings.push({
        chunkId: chunk.id,
        vector,
        model,
        dim: vector.length,
        createdAt: Date.now()
      })

      onProgress?.(i + 1, total)
    } catch (error) {
      console.error(`Failed to embed chunk ${chunk.id}:`, error)
      // Continue with other chunks
    }
  }

  return embeddings
}

/**
 * Calculate cosine similarity between two vectors
 */
export function cosineSimilarity(a: number[], b: number[]): number {
  if (a.length !== b.length) {
    throw new Error('Vectors must have the same length')
  }

  let dotProduct = 0
  let normA = 0
  let normB = 0

  for (let i = 0; i < a.length; i++) {
    dotProduct += a[i] * b[i]
    normA += a[i] * a[i]
    normB += b[i] * b[i]
  }

  return dotProduct / (Math.sqrt(normA) * Math.sqrt(normB))
}

/**
 * Find top K most similar embeddings to a query vector
 */
export function findSimilar(
  queryVector: number[],
  embeddings: Embedding[],
  topK: number = 5
): Array<{ embedding: Embedding; similarity: number }> {
  const similarities = embeddings.map(embedding => ({
    embedding,
    similarity: cosineSimilarity(queryVector, embedding.vector)
  }))

  // Sort by similarity (descending)
  similarities.sort((a, b) => b.similarity - a.similarity)

  return similarities.slice(0, topK)
}

/**
 * Embed a query and find similar chunks
 */
export async function searchSimilar(
  query: string,
  embeddings: Embedding[],
  topK: number = 5,
  model: string = 'text-embedding-3-small'
): Promise<Array<{ embedding: Embedding; similarity: number }>> {
  const queryVector = await generateEmbedding(query, model)
  return findSimilar(queryVector, embeddings, topK)
}

